import { InfiniteData, QueryClient, type QueryKey } from "react-query";
import type {
  OpenCallSearchRequest,
  OpenCallSubmission,
  OpenCallSubmissionsRequest,
  OpenCallSubmissionsResponse,
} from "../types";

const openCallKeys = {
  base: ["openCalls"] as const,
  lists: () => [...openCallKeys.base, "list"] as const,
  list: (request: OpenCallSearchRequest) =>
    [...openCallKeys.lists(), request] as const,
  paginatedLists: () => [...openCallKeys.base, "paginatedList"] as const,
  paginatedList: (request: OpenCallSearchRequest) =>
    [...openCallKeys.paginatedLists(), request] as const,
  details: () => [...openCallKeys.base, "detail"] as const,
  detail: (id?: string) => [...openCallKeys.details(), id] as const,
};

const submissionKeys = {
  base: ["submissions"] as const,
  lists: () => [...submissionKeys.base, "list"] as const,
  list: (request: OpenCallSubmissionsRequest) =>
    [...submissionKeys.lists(), request] as const,
  curation: (openCallId?: string, curatorId?: string) =>
    [...submissionKeys.lists(), { openCallId, curatorId }, "curation"] as const,
  user: (openCallId?: string, userId?: string) =>
    [...submissionKeys.lists(), { openCallId, userId }] as const,
  voting: (openCallId?: string, firstSubmissionId?: string) =>
    [
      ...submissionKeys.lists(),
      { openCallId, firstSubmissionId },
      "voting",
    ] as const,
  details: () => [...submissionKeys.base, "detail"] as const,
  // One specific submission
  detail: (openCallId?: string, submissionId?: string) =>
    [...submissionKeys.details(), openCallId, submissionId] as const,
  myArtistName: () => [...submissionKeys.base, "myArtistName"] as const,
};

const voteKeys = {
  list: () => ["openCallVoteHistory"] as const,
  detail: (id?: string) => [...openCallKeys.detail(id), "vote"] as const,
};

/*
 * Updates a submission in a cached infinite query. This is needed
 * so we can update submission without refetching the entire set of
 * data.
 */
const replaceSubmissionInQueryCache = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  update: OpenCallSubmission
) => {
  const { id: openCallId, submissionId } = update;

  const cachedSubmissions =
    queryClient.getQueryData<InfiniteData<OpenCallSubmissionsResponse>>(
      queryKey
    );

  if (cachedSubmissions?.pages) {
    const newCachedSubmissions: InfiniteData<OpenCallSubmissionsResponse> = {
      ...cachedSubmissions,
      pages: cachedSubmissions.pages.map((page) => ({
        ...page,
        data: page.data.map((submission) => {
          if (
            submission.id === openCallId &&
            submission.submissionId === submissionId
          ) {
            return update;
          }
          return submission;
        }),
      })),
    };
    queryClient.setQueriesData<InfiniteData<OpenCallSubmissionsResponse>>(
      queryKey,
      newCachedSubmissions
    );
  }
};

export {
  openCallKeys,
  submissionKeys,
  replaceSubmissionInQueryCache,
  voteKeys,
};
